import { AlertTriangle, BarChart3, CheckCircle2, Info, Table2, TrendingDown, TrendingUp, XCircle, type LucideIcon } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { Insight, Kpi, KpiFormat, Share } from '../types'
import { fmtValue } from '../visuals'
import { Sparkline } from './Charts'

export type TableSpec = { head: string[]; rows: (string | number)[][]; align?: ('left' | 'right')[] }

type CardProps = {
  title: ReactNode
  description?: ReactNode
  icon?: LucideIcon
  legend?: { label: string; color: string }[]
  table?: TableSpec
  actions?: ReactNode
  className?: string
  children: ReactNode
}

export function ReportCard({ title, description, icon: Icon, legend, table, actions, className, children }: CardProps) {
  const { t } = useTranslation()
  const [view, setView] = useState<'chart' | 'table'>('chart')
  return (
    <section className={cn('flex min-w-0 flex-col rounded-xl border bg-card shadow-card', className)}>
      <header className="flex flex-wrap items-start gap-3 px-4 pt-4 sm:px-5">
        {Icon ? (
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon className="size-4" aria-hidden />
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold tracking-tight">{title}</h2>
          {description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
        </div>
        {actions}
        {table ? (
          <div className="inline-flex rounded-lg border p-0.5" role="group" aria-label={t('reports.view.label')}>
            {(['chart', 'table'] as const).map((v) => {
              const VIcon = v === 'chart' ? BarChart3 : Table2
              return (
                <Tooltip key={v} content={t(`reports.view.${v}`)} side="top">
                  <button
                    type="button"
                    aria-pressed={view === v}
                    aria-label={t(`reports.view.${v}`)}
                    onClick={() => setView(v)}
                    className={cn('flex size-7 items-center justify-center rounded-md transition-colors', view === v ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground')}
                  >
                    <VIcon className="size-4" />
                  </button>
                </Tooltip>
              )
            })}
          </div>
        ) : null}
      </header>
      {legend && view === 'chart' ? (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 px-4 pt-3 text-xs text-muted-foreground sm:px-5">
          {legend.map((l) => (
            <li key={l.label} className="inline-flex items-center gap-1.5">
              <span className="size-2.5 rounded-sm" style={{ background: l.color }} aria-hidden />
              {l.label}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex-1 p-4 sm:p-5">{view === 'table' && table ? <DataTable spec={table} /> : children}</div>
    </section>
  )
}

export function DataTable({ spec }: { spec: TableSpec }) {
  return (
    <div className="max-h-80 overflow-auto rounded-lg border">
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            {spec.head.map((h, i) => (
              <TH key={h} className={cn('whitespace-nowrap', spec.align?.[i] === 'right' && 'text-right')}>
                {h}
              </TH>
            ))}
          </TR>
        </THead>
        <TBody>
          {spec.rows.map((r, ri) => (
            <TR key={ri}>
              {r.map((c, ci) => (
                <TD key={ci} className={cn('whitespace-nowrap text-sm', spec.align?.[ci] === 'right' && 'text-right tabular-nums')}>
                  {c}
                </TD>
              ))}
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  )
}

export function KpiCard({ kpi, label, hint }: { kpi: Kpi; label: string; hint?: string }) {
  const { t } = useTranslation()
  const d = kpi.deltaPct
  const good = d === null ? null : d >= 0 === kpi.goodWhenUp
  const Icon = d !== null && d < 0 ? TrendingDown : TrendingUp
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-xl border bg-card p-4 shadow-card">
      <p className="truncate text-sm font-medium text-muted-foreground">{label}</p>
      <div className="flex items-end justify-between gap-2">
        <p className="truncate text-2xl font-semibold tracking-tight tabular-nums">{fmtValue(kpi.value, kpi.format)}</p>
        <Sparkline values={kpi.spark} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {d === null ? (
          <span>{hint ?? t('reports.kpi.snapshot')}</span>
        ) : (
          <>
            <span className={cn('inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-semibold', good ? 'bg-success/12 text-success' : 'bg-destructive/12 text-destructive')}>
              <Icon className="size-3" aria-hidden />%{Math.abs(d).toLocaleString('tr-TR')}
            </span>
            <span>{t('reports.kpi.vsPrev')}</span>
          </>
        )}
      </div>
    </div>
  )
}

export function KpiGrid({ kpis, label, hint, className }: { kpis: Kpi[]; label: (k: string) => string; hint?: (k: string) => string | undefined; className?: string }) {
  return (
    <div className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4', className)}>
      {kpis.map((k) => (
        <KpiCard key={k.key} kpi={k} label={label(k.key)} hint={hint?.(k.key)} />
      ))}
    </div>
  )
}

export function BarList({ items, format = 'count', label = (s) => s.label, total }: { items: Share[]; format?: KpiFormat; label?: (s: Share) => string; total?: number }) {
  const { t } = useTranslation()
  if (!items.length) return <p className="py-8 text-center text-sm text-muted-foreground">{t('reports.noData')}</p>
  const max = Math.max(...items.map((i) => i.value), 1)
  const sum = total ?? items.reduce((s, i) => s + i.value, 0)
  return (
    <ul className="space-y-3">
      {items.map((i) => (
        <li key={i.key} className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate">
              {label(i)}
              {i.sub ? <span className="ml-1.5 text-xs text-muted-foreground">{i.sub}</span> : null}
            </span>
            <span className="shrink-0 tabular-nums">
              <span className="font-semibold">{fmtValue(i.value, format)}</span>
              {sum ? <span className="ml-1.5 text-xs text-muted-foreground">%{Math.round((i.value / sum) * 100)}</span> : null}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted" role="meter" aria-valuenow={i.value} aria-valuemin={0} aria-valuemax={max} aria-label={label(i)}>
            <div className="h-full rounded-full bg-[var(--series-1)] transition-[width] duration-500" style={{ width: `${Math.max(2, (i.value / max) * 100)}%` }} />
          </div>
        </li>
      ))}
    </ul>
  )
}

const INSIGHT: Record<Insight['tone'], { icon: LucideIcon; cls: string }> = {
  good: { icon: CheckCircle2, cls: 'text-success' },
  warning: { icon: AlertTriangle, cls: 'text-warning-strong' },
  bad: { icon: XCircle, cls: 'text-destructive' },
  info: { icon: Info, cls: 'text-info' },
}

export function InsightList({ items }: { items: Insight[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((i) => {
        const { icon: Icon, cls } = INSIGHT[i.tone]
        return (
          <li key={i.text} className="flex items-start gap-2.5 text-sm">
            <Icon className={cn('mt-0.5 size-4 shrink-0', cls)} aria-hidden />
            <span>{i.text}</span>
          </li>
        )
      })}
    </ul>
  )
}

export function ReportSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-28 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-80 rounded-xl" />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Skeleton className="h-72 rounded-xl" />
        <Skeleton className="h-72 rounded-xl" />
      </div>
    </div>
  )
}
